"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Switch,
  useColorModeValue,
  useToast,
  Select,
  Spinner,
  useBreakpointValue,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
  AspectRatio,
  Image,
  Container,
  Tooltip,
} from "@chakra-ui/react"
import { MdCheck, MdClose, MdDelete, MdMoreVert, MdRefresh } from "react-icons/md"
import {
  approveUser,
  listUsers,
  rejectUser,
  toggleActive,
  updateRole,
  removeUser,
} from "services/users"
import { useAuth } from "contexts/AuthContext"

export default function UserManagement() {
  const toast = useToast()
  const { user = {} } = useAuth?.() || {}
  const canGrantSuperAdmin = user?.role === "super_admin"
  const roleOptions = useMemo(() => {
    const options = ["user", "admin"]
    if (canGrantSuperAdmin) {
      options.push("super_admin")
    }
    return options
  }, [canGrantSuperAdmin])

  const bg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.600")
  const textColor = useColorModeValue("gray.700", "gray.100")
  const mutedColor = useColorModeValue("gray.500", "gray.400")
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700")
  const tableRowHoverBg = useColorModeValue("gray.50", "gray.700")

  const isMobile = useBreakpointValue({ base: true, md: false })

  const formatDateLabel = (value) => {
    if (!value) return null
    try {
      return new Date(value).toLocaleString("ko-KR")
    } catch {
      return value
    }
  }

  const getApprovalScheme = (status) => {
    if (status === "APPROVED") return "green"
    if (status === "REJECTED") return "red"
    return "orange"
  }

  const getApprovalLabel = (status) => {
    if (status === "APPROVED") return "승인"
    if (status === "REJECTED") return "반려"
    return "대기"
  }

  // 상태
  const [qInput, setQInput] = useState("") // 입력 UI
  const [q, setQ] = useState("") // 실제 쿼리
  const [page, setPage] = useState(1)
  const pageSize = 10

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [statusFilter, setStatusFilter] = useState("ALL")

  //  공통 Confirm Dialog 상태
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmTitle, setConfirmTitle] = useState("")
  const [confirmBody, setConfirmBody] = useState("")
  const [pendingAction, setPendingAction] = useState(null)
  const cancelRef = useRef()

  const askConfirm = ({ title, body, action }) => {
    setConfirmTitle(title || "변경 확인")
    setConfirmBody(body || "정말로 진행하시겠습니까?")
    setPendingAction(() => action)
    setConfirmOpen(true)
  }

  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [rejectingUser, setRejectingUser] = useState(null)
  const [rejectReason, setRejectReason] = useState("")
  const [rejectError, setRejectError] = useState("")
  const [rejecting, setRejecting] = useState(false)

  const handleConfirm = async () => {
    try {
      if (pendingAction) {
        await pendingAction()
      }
    } finally {
      setConfirmOpen(false)
      setPendingAction(null)
    }
  }

  // 검색 디바운스
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(qInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [qInput])

  // 목록 불러오기
  useEffect(() => {
    ;(async () => {
      try {
        setLoading(true)
        const params = { q, page, page_size: pageSize }
        if (statusFilter !== "ALL") {
          params.approval_status = statusFilter
        }
        const { items, total } = await listUsers(params)
        setRows(items || [])
        setTotal(total || 0)
      } catch (e) {
        const status = e?.response?.status;
        // If session expired (401/403), global handler shows a single notice. Suppress local toast.
        if (![401, 403].includes(status)) {
          toast({
            status: "error",
            title: "사용자 목록 실패",
            description: String(e?.response?.data?.detail || e?.message),
          })
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [q, page, pageSize, refreshKey, toast, statusFilter])

  const totalPages = useMemo(() => Math.max(1, Math.ceil((total || 0) / pageSize)), [total, pageSize])

  // 원래의 실행 로직은 유지 (확인은 외부 askConfirm으로)
  const onToggleActive = async (user) => {
    const next = !user.is_active
    // 낙관적 업데이트
    setRows((prev) => prev.map((x) => (x.id === user.id ? { ...x, is_active: next } : x)))
    try {
      await toggleActive(user.id, next)
      toast({ status: "success", title: next ? "활성화" : "비활성화" })
    } catch (e) {
      // 롤백
      setRows((prev) => prev.map((x) => (x.id === user.id ? { ...x, is_active: !next } : x)))
      toast({ status: "error", title: "상태 변경 실패", description: String(e?.response?.data?.detail || e?.message) })
    }
  }

  const onChangeRole = async (user, role) => {
    const old = user.role
    // 낙관적 업데이트
    setRows((prev) => prev.map((x) => (x.id === user.id ? { ...x, role } : x)))
    try {
      await updateRole(user.id, role)
      toast({ status: "success", title: "권한 변경 완료" })
    } catch (e) {
      // 롤백
      setRows((prev) => prev.map((x) => (x.id === user.id ? { ...x, role: old } : x)))
      toast({ status: "error", title: "권한 변경 실패", description: String(e?.response?.data?.detail || e?.message) })
    }
  }

  const onApprove = async (user) => {
    try {
      const updated = await approveUser(user.id)
      setRows((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
      toast({ status: "success", title: "가입 승인 완료" })
    } catch (e) {
      toast({
        status: "error",
        title: "승인 실패",
        description: String(e?.response?.data?.detail || e?.message),
      })
    }
  }

  const openRejectModal = (user) => {
    setRejectingUser(user)
    setRejectReason("")
    setRejectError("")
    setRejectModalOpen(true)
  }

  const closeRejectModal = () => {
    setRejectModalOpen(false)
    setRejectReason("")
    setRejectError("")
    setRejectingUser(null)
  }

  const confirmReject = async () => {
    if (!rejectReason.trim()) {
      setRejectError("반려 사유를 입력하세요.")
      return
    }
    if (!rejectingUser) {
      return
    }
    setRejecting(true)
    try {
      const updated = await rejectUser(rejectingUser.id, rejectReason.trim())
      setRows((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
      toast({ status: "success", title: "반려 처리 완료" })
      closeRejectModal()
    } catch (e) {
      toast({
        status: "error",
        title: "반려 실패",
        description: String(e?.response?.data?.detail || e?.message),
      })
    } finally {
      setRejecting(false)
    }
  }

  const onDelete = async (user) => {
    const prev = rows
    // 낙관적 제거
    setRows(prev.filter((x) => x.id !== user.id))
    setTotal((t) => Math.max(0, t - 1))
    try {
      await removeUser(user.id)
      toast({ status: "success", title: "삭제 완료" })
    } catch (e) {
      // 롤백
      setRows(prev)
      setTotal((t) => t + 1)
      toast({ status: "error", title: "삭제 실패", description: String(e?.response?.data?.detail || e?.message) })
    }
  }

  const UserCard = ({ user }) => {
    const isSuperUser = user.role === "super_admin"
    const roleTagColor = isSuperUser ? undefined : user.role === "admin" ? "blue" : "gray"
    return (
      <Card mb={4} shadow="sm" borderRadius="lg" border="1px solid" borderColor={borderColor} bg={bg}>
        <CardBody p={4}>
          <VStack align="stretch" spacing={3}>
            <Flex align="start" justify="space-between" gap={3}>
              <Box minW={0}>
                <Text fontWeight="700" fontSize="md" color={textColor} noOfLines={1}>
                  {user.name}
                </Text>
                <Text fontSize="xs" color={mutedColor}>
                  ID: {user.id}
                </Text>
              </Box>

              <HStack spacing={1}>
                <Switch
                  isChecked={user.is_active}
                  isDisabled={isSuperUser}
                  onChange={() => {
                    if (isSuperUser) return
                    askConfirm({
                      title: "상태 변경 확인",
                      body: `${user.email} 사용자를 ${user.is_active ? "비활성화" : "활성화"} 하시겠습니까?`,
                      action: () => onToggleActive(user),
                    })
                  }}
                  colorScheme="blue"
                  size="sm"
                />
                <Menu placement="bottom-end" isLazy>
                  <Tooltip label="작업" placement="top">
                    <MenuButton
                      as={IconButton}
                      aria-label="사용자 작업"
                      icon={<MdMoreVert />}
                      size="sm"
                      variant="ghost"
                    />
                  </Tooltip>
                  <MenuList minW="180px">
                    <MenuItem
                      icon={<MdCheck />}
                      isDisabled={isSuperUser || user.approval_status === "APPROVED"}
                      onClick={() => {
                        if (isSuperUser) return
                        askConfirm({
                          title: "가입 승인",
                          body: `${user.email} 사용자를 승인하시겠습니까?`,
                          action: () => onApprove(user),
                        })
                      }}
                    >
                      승인
                    </MenuItem>
                    <MenuItem
                      icon={<MdClose />}
                      isDisabled={isSuperUser}
                      onClick={() => {
                        if (isSuperUser) return
                        openRejectModal(user)
                      }}
                    >
                      반려
                    </MenuItem>
                    <MenuItem
                      icon={<MdDelete />}
                      isDisabled={isSuperUser}
                      onClick={() => {
                        if (isSuperUser) return
                        askConfirm({
                          title: "삭제 확인",
                          body: `${user.email} 사용자를 삭제하시겠습니까?`,
                          action: () => onDelete(user),
                        })
                      }}
                    >
                      삭제
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            </Flex>

            <Grid templateColumns="88px 1fr" columnGap={3} rowGap={2}>
              <Text fontSize="xs" color={mutedColor} fontWeight="600">
                이메일
              </Text>
              <Text fontSize="sm" color={textColor} noOfLines={1}>
                {user.email}
              </Text>

              <Text fontSize="xs" color={mutedColor} fontWeight="600">
                승인상태
              </Text>
              <HStack spacing={2} minW={0}>
                <Tag size="sm" colorScheme={getApprovalScheme(user.approval_status)} borderRadius="md">
                  {getApprovalLabel(user.approval_status)}
                </Tag>
                {user.approval_status === "APPROVED" && user.approved_at && (
                  <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                    {formatDateLabel(user.approved_at)}
                  </Text>
                )}
                {user.approval_status === "REJECTED" && user.rejected_at && (
                  <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                    {formatDateLabel(user.rejected_at)}
                  </Text>
                )}
              </HStack>

              <Text fontSize="xs" color={mutedColor} fontWeight="600">
                권한
              </Text>
              <HStack spacing={2} align="center">
                <Tag
                  size="sm"
                  borderRadius="md"
                  colorScheme={roleTagColor}
                  bg={isSuperUser ? "yellow.300" : undefined}
                  color={isSuperUser ? "black" : undefined}
                  fontWeight={isSuperUser ? "700" : "500"}
                  textTransform="none"
                >
                  {isSuperUser ? "super admin" : user.role}
                </Tag>
                <Select
                  size="xs"
                  w="110px"
                  value={user.role}
                  isDisabled={isSuperUser}
                  onChange={(e) => {
                    if (isSuperUser) return
                    const next = e.target.value
                    if (next === user.role) return
                    askConfirm({
                      title: "권한 변경 확인",
                      body: `${user.email}\n권한을 ${user.role} → ${next} 로 변경하시겠습니까?`,
                      action: () => onChangeRole(user, next),
                    })
                  }}
                  borderRadius="md"
                  borderColor={borderColor}
                  bg={bg}
                >
                  {roleOptions.map((entry) => (
                    <option key={entry} value={entry}>
                      {entry === "super_admin" ? "super admin" : entry}
                    </option>
                  ))}
                </Select>
              </HStack>

              {user.approval_status === "REJECTED" && user.approval_reason && (
                <>
                  <Text fontSize="xs" color={mutedColor} fontWeight="600">
                    반려사유
                  </Text>
                  <Text fontSize="xs" color="red.500">
                    {user.approval_reason}
                  </Text>
                </>
              )}
            </Grid>
          </VStack>
        </CardBody>
      </Card>
    )
  }

  return (
    <Box minH="100vh" bg="white">
      <Box w="100%">
        {/* 모바일은 더 높은 비율 / 데스크탑은 5:1 유지 */}
        <AspectRatio ratio={{ base: 2.2, sm: 4, md: 5 }} w="100%">
          <Box position="relative" w="100%" h="100%">
            {/* 배경 이미지 */}
            <Image
              src="/usermanager.png"
              alt="사용자 관리 배너"
              w="100%"
              h="100%"
              objectFit="cover"
              draggable={false}
              userSelect="none"
            />

            {/* 오버레이 */}
            <Box
              position="absolute"
              inset={0}
              bg={{
                base: "linear-gradient(90deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.22) 60%, rgba(0,0,0,0.12) 100%)",
                sm:   "linear-gradient(90deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.15) 60%, rgba(0,0,0,0.10) 100%)",
              }}
            />

            {/* 콘텐츠 레이어 */}
            <Container
              maxW="7xl"
              position="absolute"
              inset={0}
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              px={{ base: 4, sm: 6, md: 8 }}
            >
              <Flex w="full" h="full" align="center">
                <VStack
                  spacing={{ base: 2, md: 3 }}
                  align="flex-start"
                  maxW={{ base: "92%", md: "50%" }}
                  color="white"
                >
                  {/* 제목 */}
                  <Heading
                    as="h1"
                    fontWeight="800"
                    letterSpacing="-0.01em"
                    lineHeight="1.1"
                    fontSize={{ base: "2xl", sm: "3xl", md: "4xl", lg: "5xl" }}
                  >
                    사용자 관리
                  </Heading>

                  {/* 설명: 데스크탑/태블릿에서만 노출 */}
                  {!isMobile && (
                    <Text
                      fontSize={{ sm: "sm", md: "sm" }}
                      lineHeight={{ sm: "1.6", md: "1.6" }}
                      opacity={0.95}
                    >
                      시스템 사용자를 효율적으로 관리하고 권한을 설정할 수 있는 통합 관리 시스템입니다.
                    </Text>
                  )}
                </VStack>
              </Flex>
            </Container>
          </Box>
        </AspectRatio>
      </Box>
      <Box position="relative" 
      // mt="-120px" zIndex={3} 
      mt="30px"
      px={{ base: 4, md: 8 }} pb={16}>
        <Box
          bg="white"
          borderRadius="2xl"
          shadow="2xl"
          border="1px solid"
          borderColor="gray.100"
          p={{ base: 6, md: 8 }}
          maxW="7xl"
          mx="auto"
        >
        <VStack spacing={6} mb={8}>
          <Flex justify="flex-end" w="full" gap={3}>
            {/* 새로고침 버튼 */}
            <Button
              leftIcon={<MdRefresh />}     // RepeatIcon 대신 MdRefresh도 사용 가능
              onClick={() => setRefreshKey((k) => k + 1)}
              colorScheme="blue"
              size={{ base: "md", md: "lg" }}
              shadow="md"
              _hover={{ transform: "translateY(-2px)", shadow: "lg" }}
              transition="all 0.2s"
              w={{ base: "full", md: "auto" }}
              rounded="xl"
            >
              새로고침
            </Button>

            {/* 초기화 버튼 */}
            <Button
              leftIcon={<MdDelete />}
              onClick={() => {
                setQInput("")
                setQ("")
                setPage(1)
              }}
              colorScheme="blue"
              size={{ base: "md", md: "lg" }}
              shadow="md"
              _hover={{ transform: "translateY(-2px)", shadow: "lg" }}
              transition="all 0.2s"
              w={{ base: "full", md: "auto" }}
              rounded="xl"
            >
              초기화
            </Button>
          </Flex>
        </VStack>

        {/* 검색 인풋은 따로 */}
        <Flex gap={4} mb={6} wrap="wrap">
          <Input
            placeholder="이름/이메일/부서 검색"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            borderRadius="md"
            borderColor={borderColor}
            bg={bg}
            flex="1"
            minW="250px"
            h="10"
          />
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
            borderRadius="md"
            borderColor={borderColor}
            bg={bg}
            w={{ base: "100%", md: "200px" }}
          >
            <option value="ALL">상태 전체</option>
            <option value="PENDING">대기 중</option>
            <option value="APPROVED">승인됨</option>
            <option value="REJECTED">반려됨</option>
          </Select>
        </Flex>

          {isMobile ? (
            <Box>
              {loading ? (
                <Flex align="center" justify="center" h="300px">
                  <Spinner size="lg" color="blue.500" />
                </Flex>
              ) : (
                <VStack spacing={3}>
                  {rows.map((user) => (
                    <UserCard key={user.id} user={user} />
                  ))}
                  {!rows.length && (
                    <Box p={12} textAlign="center" color={mutedColor}>
                      <Text fontSize="lg">데이터가 없습니다.</Text>
                    </Box>
                  )}
                </VStack>
              )}
            </Box>
          ) : (
            <Box overflow="auto" border="1px solid" borderColor={borderColor} rounded="lg" minH="300px" bg={bg}>
              {loading ? (
                <Flex align="center" justify="center" h="300px">
                  <Spinner size="lg" color="blue.500" />
                </Flex>
              ) : (
                <Table size="sm" variant="simple">
                  <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th color={textColor} fontSize="xs" fontWeight="700" whiteSpace="nowrap">
                      ID
                    </Th>
                    <Th color={textColor} fontSize="xs" fontWeight="700" whiteSpace="nowrap">
                      이름
                    </Th>
                    <Th color={textColor} fontSize="xs" fontWeight="700" whiteSpace="nowrap">
                      이메일
                    </Th>
                    <Th color={textColor} fontSize="xs" fontWeight="700" whiteSpace="nowrap">
                      승인상태
                    </Th>
                    <Th color={textColor} fontSize="xs" fontWeight="700" whiteSpace="nowrap">
                      권한
                    </Th>
                    <Th color={textColor} fontSize="xs" fontWeight="700" whiteSpace="nowrap">
                      활성
                    </Th>
                    <Th color={textColor} fontSize="xs" fontWeight="700" whiteSpace="nowrap" textAlign="right">
                      액션
                    </Th>
                  </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((r) => {
                      const isSuperUser = r.role === "super_admin"
                      const roleTagColor = isSuperUser ? undefined : r.role === "admin" ? "blue" : "gray"
                      return (
                      <Tr key={r.id} _hover={{ bg: tableRowHoverBg }}>
                        <Td color={textColor} fontSize="sm" whiteSpace="nowrap">
                          {r.id}
                        </Td>
                        <Td fontSize="sm" color={textColor} maxW="180px">
                          <Text fontWeight="600" noOfLines={1}>
                            {r.name}
                          </Text>
                        </Td>
                        <Td fontSize="sm" color={textColor} maxW="260px">
                          <Text noOfLines={1}>{r.email}</Text>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Tag size="sm" colorScheme={getApprovalScheme(r.approval_status)} borderRadius="md">
                              {getApprovalLabel(r.approval_status)}
                            </Tag>
                            {r.approval_status === "APPROVED" && r.approved_at && (
                              <Text fontSize="xs" color={mutedColor}>
                                승인: {formatDateLabel(r.approved_at)}
                              </Text>
                            )}
                            {r.approval_status === "REJECTED" && (
                              <>
                                {r.approval_reason && (
                                  <Text fontSize="xs" color="red.500" noOfLines={2} maxW="240px">
                                    사유: {r.approval_reason}
                                  </Text>
                                )}
                                {r.rejected_at && (
                                  <Text fontSize="xs" color={mutedColor}>
                                    {formatDateLabel(r.rejected_at)}
                                  </Text>
                                )}
                              </>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={2}>
                          <Tag
                            size="sm"
                            borderRadius="md"
                            colorScheme={roleTagColor}
                            bg={isSuperUser ? "yellow.300" : undefined}
                            color={isSuperUser ? "black" : undefined}
                            fontWeight={isSuperUser ? "700" : "500"}
                            textTransform="none"
                          >
                            {isSuperUser ? "super admin" : r.role}
                          </Tag>
                          <Select
                            size="xs"
                            w="110px"
                            value={r.role}
                            isDisabled={isSuperUser}
                            onChange={(e) => {
                              if (isSuperUser) return
                              const next = e.target.value
                              if (next === r.role) return
                              askConfirm({
                                title: "권한 변경 확인",
                                body: `${r.email}\n권한을 ${r.role} → ${next} 로 변경하시겠습니까?`,
                                action: () => onChangeRole(r, next),
                              })
                            }}
                            borderRadius="md"
                            borderColor={borderColor}
                            bg={bg}
                          >
                            {roleOptions.map((entry) => (
                              <option key={entry} value={entry}>
                                {entry === "super_admin" ? "super admin" : entry}
                              </option>
                            ))}
                          </Select>
                          </VStack>
                        </Td>
                        <Td>
                        <Switch
                          isChecked={r.is_active}
                          isDisabled={isSuperUser}
                          onChange={() => {
                            if (isSuperUser) return
                            askConfirm({
                              title: "상태 변경 확인",
                              body: `${r.email} 사용자를 ${r.is_active ? "비활성화" : "활성화"} 하시겠습니까?`,
                              action: () => onToggleActive(r),
                            })
                          }}
                          colorScheme="blue"
                          size="sm"
                        />
                        </Td>
                        <Td>
                          <HStack spacing={1} justify="flex-end">
                            <Menu placement="bottom-end" isLazy>
                              <Tooltip label="작업" placement="top">
                                <MenuButton
                                  as={IconButton}
                                  aria-label="사용자 작업"
                                  icon={<MdMoreVert />}
                                  size="sm"
                                  variant="ghost"
                                  isDisabled={isSuperUser}
                                />
                              </Tooltip>
                              <MenuList minW="180px">
                                <MenuItem
                                  icon={<MdCheck />}
                                  isDisabled={isSuperUser || r.approval_status === "APPROVED"}
                                  onClick={() => {
                                    if (isSuperUser) return
                                    askConfirm({
                                      title: "가입 승인",
                                      body: `${r.email} 사용자를 승인하시겠습니까?`,
                                      action: () => onApprove(r),
                                    })
                                  }}
                                >
                                  승인
                                </MenuItem>
                                <MenuItem
                                  icon={<MdClose />}
                                  isDisabled={isSuperUser}
                                  onClick={() => {
                                    if (isSuperUser) return
                                    openRejectModal(r)
                                  }}
                                >
                                  반려
                                </MenuItem>
                                <MenuItem
                                  icon={<MdDelete />}
                                  isDisabled={isSuperUser}
                                  onClick={() => {
                                    if (isSuperUser) return
                                    askConfirm({
                                      title: "삭제 확인",
                                      body: `${r.email} 사용자를 삭제하시겠습니까?`,
                                      action: () => onDelete(r),
                                    })
                                  }}
                                >
                                  삭제
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </HStack>
                          </Td>
                        </Tr>
                      );
                    })}
                    {!rows.length && (
                      <Tr>
                        <Td colSpan={7}>
                          <Box p={12} textAlign="center" color={mutedColor}>
                            <Text fontSize="lg">데이터가 없습니다.</Text>
                          </Box>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              )}
            </Box>
          )}

          {isMobile ? (
            <VStack
              mt={6}
              spacing={3}
              align="stretch"
              p={4}
              bg={tableHeaderBg}
              borderRadius="md"
              border="1px solid"
              borderColor={borderColor}
            >
              <Text fontSize="sm" color={mutedColor} fontWeight="500">
                총 {total}명
              </Text>
              <Flex
                w="100%"
                direction="column"
                align="center"
                gap={3}
                mt={3}
              >
                <HStack
                  spacing={3}
                  w="100%"
                  justify="center"
                  flexWrap="wrap"
                  align="stretch"
                >
                  <Button
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    isDisabled={page === 1}
                    variant="outline"
                    borderRadius="md"
                    borderColor={borderColor}
                    flex="1"
                    minW="0"
                    w="100%"
                  >
                    이전
                  </Button>
                  <Box
                    fontSize="sm"
                    textAlign="center"
                    fontWeight="600"
                    color={textColor}
                    px={3}
                    py={2}
                    bg={bg}
                    borderRadius="md"
                    border="1px solid"
                    borderColor={borderColor}
                    flex="1"
                    minW="0"
                  >
                    {page} / {totalPages}
                  </Box>
                  <Button
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    isDisabled={page >= totalPages}
                    variant="outline"
                    borderRadius="md"
                    borderColor={borderColor}
                    flex="1"
                    minW="0"
                    w="100%"
                  >
                    다음
                  </Button>
                </HStack>
              </Flex>
            </VStack>
          ) : (
            <Flex
              justify="space-between"
              align="center"
              mt={6}
              wrap="wrap"
              gap={4}
              p={4}
              bg={tableHeaderBg}
              borderRadius="md"
              border="1px solid"
              borderColor={borderColor}
            >
              <Text fontSize="sm" color={mutedColor} fontWeight="500">
                총 {total}명
              </Text>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  isDisabled={page === 1}
                  variant="outline"
                  borderRadius="md"
                  borderColor={borderColor}
                >
                  이전
                </Button>
                <Box
                  fontSize="sm"
                  minW="80px"
                  textAlign="center"
                  fontWeight="600"
                  color={textColor}
                  px={3}
                  py={1}
                  bg={bg}
                  borderRadius="md"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  {page} / {totalPages}
                </Box>
                <Button
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  isDisabled={page >= totalPages}
                  variant="outline"
                  borderRadius="md"
                  borderColor={borderColor}
                >
                  다음
                </Button>
              </HStack>
            </Flex>
          )}
        </Box>
      </Box>

      {/*  공통 Confirm Dialog */}
      <AlertDialog
        isOpen={confirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {confirmTitle}
            </AlertDialogHeader>

            <AlertDialogBody whiteSpace="pre-line">{confirmBody}</AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirmOpen(false)}>
                취소
              </Button>
              <Button colorScheme="blue" onClick={handleConfirm} ml={3}>
                확인
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <Modal isOpen={rejectModalOpen} onClose={closeRejectModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>사용자 반려</ModalHeader>
          <ModalBody>
            <Text fontWeight="600" mb={2}>
              {rejectingUser?.name} ({rejectingUser?.email})
            </Text>
            <Textarea
              value={rejectReason}
              onChange={(e) => {
                setRejectReason(e.target.value)
                setRejectError("")
              }}
              placeholder="반려 사유를 입력하세요."
              size="sm"
              minH="120px"
            />
            {rejectError && (
              <Text color="red.500" fontSize="sm" mt={2}>
                {rejectError}
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeRejectModal}>취소</Button>
            <Button colorScheme="red" onClick={confirmReject} ml={3} isLoading={rejecting}>
              반려 처리
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
